/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useState } from 'react';

/* Import components */
import Api2TimeSeriesPlot from '@/components/api/Api2TimeSeriesPlot';
import CodeBlock from '@/components/code/CodeBlock';
import LayoutSectionHeader from '@/components/tabs/layouts/LayoutSectionHeader';
import StaticTimeSeriesPlot from '@/components/charts/StaticTimeSeriesPlot';

/* Import configuration */
import {
    API_TIME_SERIES_PLOT_EXAMPLES,
    EXAMPLE_STATIC_PARSED_TIME_SERIES,
    EXAMPLE_STATIC_TIME_SERIES_PLOT_TYPES,
} from '@/components/tabs/layouts/layoutExamples';

/* Import libraries */
import type { TimeSeriesPlotType } from '@/lib/timeSeries/timeSeriesPlotly';

interface TimeSeriesPlotLayoutsTabProps {
    theme: 'light' | 'dark';
}

function TimeSeriesPlotLayoutsTab({ theme }: TimeSeriesPlotLayoutsTabProps) {
    const [selectedStaticTimeSeriesPlotType, setSelectedStaticTimeSeriesPlotType] =
        useState<TimeSeriesPlotType>('line');
    const [staticTimeSeriesCodeTab, setStaticTimeSeriesCodeTab] =
        useState<'jsx' | 'data'>('jsx');
    const [selectedApiTimeSeriesPlotExampleIndex, setSelectedApiTimeSeriesPlotExampleIndex] =
        useState(0);

    const selectedApiTimeSeriesPlotExample =
        API_TIME_SERIES_PLOT_EXAMPLES[selectedApiTimeSeriesPlotExampleIndex];
    const selectedStaticTimeSeriesVariableName = {
        line: 'lineChartParsedTimeSeries',
        stackedBar: 'stackedBarChartParsedTimeSeries',
        groupedBar: 'groupedBarChartParsedTimeSeries',
    } satisfies Record<TimeSeriesPlotType, string>;
    const selectedStaticTimeSeriesCodeName =
        selectedStaticTimeSeriesVariableName[selectedStaticTimeSeriesPlotType];
    const selectedStaticTimeSeriesCode = `const ${selectedStaticTimeSeriesCodeName} = ${JSON.stringify(
        EXAMPLE_STATIC_PARSED_TIME_SERIES,
        null,
        4,
    )};`;

    return (
        <div className="layouts-tab">
            <LayoutSectionHeader
                sectionKey="time-series-plot"
                description="TimeSeriesPlot renders parsed time series data as a Plotly chart with a shared figure presentation. It can receive prepared data directly or load and parse CSV data from API2 first."
            />

            <section className="layouts-section">
                <h3>StaticTimeSeriesPlot</h3>
                <p>
                    Renders a plot from a manually created ParsedTimeSeriesCsv object.
                </p>
                <div className="inline-flex flex-wrap items-center gap-3">
                    <label
                        className="text-sm font-semibold"
                        htmlFor="static-time-series-plot-type"
                    >
                        Time series chart type
                    </label>
                    <select
                        id="static-time-series-plot-type"
                        className="select select-bordered w-auto"
                        value={selectedStaticTimeSeriesPlotType}
                        onChange={(event) => {
                            setSelectedStaticTimeSeriesPlotType(
                                event.target.value as TimeSeriesPlotType,
                            );
                        }}
                    >
                        {EXAMPLE_STATIC_TIME_SERIES_PLOT_TYPES.map((chartType) => (
                            <option key={chartType.value} value={chartType.value}>
                                {chartType.label}
                            </option>
                        ))}
                    </select>
                </div>
                <h4>Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift">
                        <button
                            type="button"
                            className={`tab ${staticTimeSeriesCodeTab === 'jsx' ? 'tab-active' : ''}`}
                            onClick={() => {
                                setStaticTimeSeriesCodeTab('jsx');
                            }}
                        >JSX</button>
                        <button
                            type="button"
                            className={`tab ${staticTimeSeriesCodeTab === 'data' ? 'tab-active' : ''}`}
                            onClick={() => {
                                setStaticTimeSeriesCodeTab('data');
                            }}
                        >Data</button>
                    </div>
                    <CodeBlock
                        className="layouts-code--tabbed"
                        code={staticTimeSeriesCodeTab === 'data'
                            ? selectedStaticTimeSeriesCode
                            : `<StaticTimeSeriesPlot
    parsedTimeSeries={${selectedStaticTimeSeriesCodeName}}
    chartType="${selectedStaticTimeSeriesPlotType}"
    title="Static time series example"
    xAxisTitle="Period"
    yAxisTitle="Energy in kWh"
/>`}
                        language={staticTimeSeriesCodeTab === 'data' ? 'typescript' : 'jsx'}
                        roundedTop={false}
                        theme={theme}
                    />
                </div>
                <h4>Rendered preview</h4>
                <section className="dashboard-panel dashboard-panel--chart layouts-demo-panel layouts-demo-panel--plot">
                    <StaticTimeSeriesPlot
                        chartType={selectedStaticTimeSeriesPlotType}
                        fillParent
                        parsedTimeSeries={EXAMPLE_STATIC_PARSED_TIME_SERIES}
                        title="Static time series example"
                        xAxisTitle="Period"
                        yAxisTitle="Energy in kWh"
                    />
                </section>
            </section>

            <section className="layouts-section">
                <h3>Api2TimeSeriesPlot</h3>
                <p>
                    Loads API2 CSV data, parses it and renders the plot with shared presentation.
                </p>
                <div className="inline-flex flex-wrap items-center gap-3">
                    <label
                        className="text-sm font-semibold"
                        htmlFor="api-time-series-plot-example"
                    >
                        API time series example
                    </label>
                    <select
                        id="api-time-series-plot-example"
                        className="select select-bordered w-auto"
                        value={selectedApiTimeSeriesPlotExampleIndex}
                        onChange={(event) => {
                            setSelectedApiTimeSeriesPlotExampleIndex(Number(event.target.value));
                        }}
                    >
                        {API_TIME_SERIES_PLOT_EXAMPLES.map((example, index) => (
                            <option key={example.label} value={index}>
                                {example.label}
                            </option>
                        ))}
                    </select>
                </div>
                <h4>Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift">
                        <button type="button" className="tab tab-active">JSX</button>
                    </div>
                    <CodeBlock
                        className="layouts-code--tabbed"
                        roundedTop={false}
                        code={selectedApiTimeSeriesPlotExample.code}
                        theme={theme}
                    />
                </div>
                <h4>Rendered preview</h4>
                <section className="dashboard-panel dashboard-panel--chart layouts-demo-panel layouts-demo-panel--plot">
                    <Api2TimeSeriesPlot
                        from="NOW-3MONTHS"
                        to="NOW"
                        urn={selectedApiTimeSeriesPlotExample.props.urn}
                        bandMarkers={selectedApiTimeSeriesPlotExample.props.bandMarkers}
                        right={selectedApiTimeSeriesPlotExample.props.right}
                        title="PV prediction GN.Z01.P"
                        xAxisTitle="Period"
                        yAxisTitle="Power in kWh"
                    />
                </section>
            </section>
        </div>
    );
}

export default TimeSeriesPlotLayoutsTab;
